import {Component, HostBinding, HostListener, Input, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {AppSidebarService} from '../../prime/app-sidebar.service';

@Component({
	standalone: false,
	selector: 'app-sidebar',
	styles: [`
		:host {
			display: flex;
			align-self: stretch;
			min-height: 100%;
		}

		@media (min-width: 992px) {
			:host {
				position: sticky;
				top: 0;
				align-self: flex-start;
				height: 100dvh;
				max-height: 100dvh;
			}
		}

		@media (max-width: 991.98px) {
			:host {
				position: fixed;
				inset: 0;
				z-index: 1050;
				width: 100%;
				height: 100%;
				flex: none;
				min-height: 0;
				pointer-events: none;
			}

			:host(.sidebar-open) {
				pointer-events: auto;
			}
		}
	`],
	template: `
		<div
			*ngIf="overlayOpen"
			class="app-sidebar-backdrop"
			(click)="closeSidebar()"
			aria-hidden="true"></div>
		<aside
			class="app-sidebar menu-sidebar"
			data-tour="app-sidebar"
			[class.compacted]="compacted"
			[class.collapsed]="collapsed"
			[class.hover-expanded]="hoverExpanded">
			<a class="app-sidebar-brand" routerLink="/" (click)="onBrandClick($event)">
				<groin-logo class="app-sidebar-brand__logo"></groin-logo>
				<span class="app-sidebar-brand__text">
					<span class="app-sidebar-brand__title">Groin</span>
					<span class="app-sidebar-brand__subtitle">le 17.45</span>
				</span>
			</a>
			<div class="scrollable">
				<ng-content></ng-content>
			</div>
		</aside>
	`,
})
export class AppSidebarComponent implements OnInit, OnDestroy {
	@Input() tag = 'menu-sidebar';
	@HostBinding('class.sidebar-open') sidebarOpen = false;
	@HostBinding('class.sidebar-overlay-mode') get sidebarOverlayMode(): boolean {
		return this.overlayOpen;
	}
	compacted = false;
	collapsed = false;
	hoverExpanded = false;
	private subscription?: Subscription;
	private hoverSub?: Subscription;

	constructor(private readonly sidebarService: AppSidebarService) {
	}

	get overlayOpen(): boolean {
		if (this.sidebarService.isCollapsedOverlayMode()) {
			return !this.collapsed;
		}
		if (this.sidebarService.isTabletSidebarViewport()) {
			return !this.compacted && !this.collapsed;
		}
		return false;
	}

	ngOnInit(): void {
		this.subscription = this.sidebarService.getSidebarState(this.tag).subscribe((state) => {
			this.compacted = state === 'compacted';
			this.collapsed = state === 'collapsed';
			this.sidebarOpen = this.overlayOpen;
			if (!this.compacted) {
				this.sidebarService.setHoverPeek(false);
			}
		});
		this.hoverSub = this.sidebarService.getHoverPeek().subscribe((active) => {
			this.hoverExpanded = active;
		});
	}

	ngOnDestroy(): void {
		this.subscription?.unsubscribe();
		this.hoverSub?.unsubscribe();
	}

	@HostListener('mouseenter')
	onSidebarEnter(): void {
		if (!this.sidebarService.isMobileViewport()
			&& !this.sidebarService.isTabletSidebarViewport()
			&& this.compacted) {
			this.sidebarService.setHoverPeek(true);
		}
	}

	@HostListener('mouseleave')
	onSidebarLeave(): void {
		this.sidebarService.setHoverPeek(false);
	}

	@HostListener('document:keydown.escape')
	onEscape(): void {
		if (this.overlayOpen) {
			this.closeSidebar();
		}
	}

	closeSidebar(): void {
		if (this.overlayOpen) {
			this.sidebarService.collapseExpandedOverlay(this.tag);
		}
	}

	onBrandClick(event: Event): void {
		if (this.sidebarService.isCollapsedOverlayMode()) {
			this.sidebarService.collapse(this.tag);
			return;
		}
		if (this.sidebarService.isTabletSidebarViewport() && this.compacted) {
			event.preventDefault();
			this.sidebarService.expand(this.tag);
		}
	}
}
